import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Award } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { copy } from "../data/copy";
import { BUSINESS } from "../data/copy";

const reveal = {
    hidden: { y: "110%" },
    show: (i) => ({
        y: "0%",
        transition: { delay: 0.35 + i * 0.08, duration: 1.05, ease: [0.19, 1, 0.22, 1] },
    }),
};

const Line = ({ i, children, className = "", telugu = false }) => (
    <span className="mask-line block">
        <motion.span
            custom={i}
            variants={reveal}
            initial="hidden"
            animate="show"
            className={`inline-block ${telugu ? "font-telugu-display" : "font-display"} ${className}`}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const { lang, t } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

    const scrollToPickles = () => document.getElementById("pickles")?.scrollIntoView({ behavior: "smooth" });
    const scrollToStory = () => document.getElementById("story")?.scrollIntoView({ behavior: "smooth" });

    return (
        <section
            ref={ref}
            id="hero"
            className="relative min-h-[100svh] w-full overflow-hidden pt-24 md:pt-28 pb-16"
            data-testid="hero-section"
        >
            {/* Soft blob background */}
            <motion.div
                style={{ y: bgY }}
                className="pointer-events-none absolute -top-32 -right-24 w-[560px] h-[560px] rounded-full opacity-70"
                aria-hidden="true"
            >
                <div className="w-full h-full rounded-full" style={{
                    background: "radial-gradient(circle at 30% 30%, var(--kp-turmeric) 0%, transparent 60%)",
                }} />
            </motion.div>
            <div
                className="pointer-events-none absolute bottom-0 -left-20 w-[380px] h-[380px] rounded-full opacity-60"
                aria-hidden="true"
                style={{ background: "radial-gradient(circle at 50% 50%, rgba(163, 181, 101, 0.55) 0%, transparent 60%)" }}
            />

            <div className="max-w-[1300px] mx-auto px-6 md:px-10 relative z-10 grid grid-cols-12 gap-6 md:gap-10 items-center">
                {/* Left: copy */}
                <div className="col-span-12 md:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.7 }}
                        className="flex items-center gap-3 mb-6"
                    >
                        <span className="sticker" data-testid="hero-eyebrow">
                            {t({ en: "Made with love in Guntur", te: "గుంటూరులో ప్రేమతో" })}
                        </span>
                        <span className="hidden sm:inline text-xs text-[color:var(--kp-ink-soft)]/70 font-body">
                            {t({ en: "Est. 2000 · Family recipes", te: "2000 నుండి · కుటుంబ వంటకాలు" })}
                        </span>
                    </motion.div>

                    <h1
                        className="text-[13vw] md:text-[8.5vw] leading-[0.95] tracking-editorial mb-6"
                        data-testid="hero-title"
                    >
                        {lang === "en" ? (
                            <>
                                <Line i={0}>Taste that </Line>
                                <Line i={1}>
                                    <span className="wavy-underline">feels like</span>
                                </Line>
                                <Line i={2}>
                                    <em className="italic text-[color:var(--kp-terracotta)] font-medium">home.</em>
                                </Line>
                            </>
                        ) : (
                            <>
                                <Line i={0} telugu>ఇంటి రుచికి</Line>
                                <Line i={1} telugu>
                                    <span className="wavy-underline">సరిపడ్డ</span>
                                </Line>
                                <Line i={2} telugu>
                                    <em className="text-[color:var(--kp-terracotta)]">ఊరగాయలు.</em>
                                </Line>
                            </>
                        )}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.7 }}
                        className={`text-lg md:text-xl text-[color:var(--kp-ink-soft)] leading-relaxed max-w-lg mb-8 ${
                            lang === "te" ? "font-telugu-body" : "font-body"
                        }`}
                        data-testid="hero-sub"
                    >
                        {t({
                            en: "Sun-cured mangoes, cold-pressed oil, and Kumari Amma's hands. No preservatives, no shortcuts — just three generations of Andhra pantry.",
                            te: "ఎండలో ఉంచిన మామిడికాయలు, ఘాణా నూనె, కుమారి అమ్మ చేతి రుచి. కృత్రిమ పదార్థాలు లేవు — మూడు తరాల ఆంధ్ర ఇంటి రుచి.",
                        })}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.05, duration: 0.7 }}
                        className="flex flex-wrap gap-3 items-center"
                    >
                        <button className="btn-warm" onClick={scrollToPickles} data-testid="hero-cta-shop">
                            {t({ en: "Shop the pickles", te: "ఊరగాయలు కొనండి" })}
                            <ArrowRight size={16} />
                        </button>
                        <button className="btn-outline-warm" onClick={scrollToStory} data-testid="hero-cta-story">
                            {t({ en: "Meet Kumari Amma", te: "కుమారి అమ్మ" })}
                        </button>
                        <div className="ml-1 flex items-center gap-2 text-sm text-[color:var(--kp-ink-soft)]">
                            <Award size={16} className="text-[color:var(--kp-banana)]" />
                            <span className="font-body">
                                <span className="font-bold">{t({ en: "Licensed home kitchen", te: "అనుమతి పొందిన ఇంటి వంటగది" })}</span>
                            </span>
                        </div>
                    </motion.div>

                    {/* Handwritten arrow scribble */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.3, duration: 0.7 }}
                        className="corner-arrow mt-10 hidden md:flex items-center gap-2 text-[color:var(--kp-brown)]"
                    >
                        <span className="font-handwritten text-2xl -rotate-3">
                            {t({ en: "start with the mango", te: "మామిడితో మొదలుపెట్టండి" })}
                        </span>
                        <svg width="80" height="30" viewBox="0 0 80 30" fill="none">
                            <path d="M2 15 Q 20 5, 40 12 T 78 20" stroke="var(--kp-brown)" strokeWidth="2" strokeLinecap="round" fill="none" />
                            <path d="M74 15 L 78 20 L 72 22" stroke="var(--kp-brown)" strokeWidth="2" strokeLinecap="round" fill="none" />
                        </svg>
                    </motion.div>
                </div>

                {/* Right: photograph in rounded frame with stickers */}
                <div className="col-span-12 md:col-span-5 relative">
                    <div className="relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ delay: 0.4, duration: 1, ease: [0.19, 1, 0.22, 1] }}
                            className="round-frame aspect-[4/5] card-warm"
                            style={{ borderRadius: 24 }}
                        >
                            <motion.img
                                style={{ y: imgY }}
                                src="/img/jars.jpg"
                                alt="Traditional Andhra pickles"
                                className="absolute inset-0 w-full h-full object-cover"
                                data-testid="hero-image"
                            />
                        </motion.div>

                        {/* Floating tag stickers */}
                        <motion.div
                            initial={{ opacity: 0, y: 20, rotate: -8 }}
                            animate={{ opacity: 1, y: 0, rotate: -6 }}
                            transition={{ delay: 1.1, duration: 0.7 }}
                            className="absolute -top-4 -left-4 sticker"
                            style={{ background: "var(--kp-banana)", color: "var(--kp-cream)", boxShadow: "0 2px 0 #4c6a1e" }}
                        >
                            {t({ en: "100% Home-made", te: "100% ఇంటి తయారీ" })}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20, rotate: 8 }}
                            animate={{ opacity: 1, y: 0, rotate: 5 }}
                            transition={{ delay: 1.25, duration: 0.7 }}
                            className="absolute -bottom-4 -right-4 sticker"
                            style={{ background: "var(--kp-terracotta)", color: "var(--kp-cream)", boxShadow: "0 2px 0 var(--kp-terracotta-deep)" }}
                        >
                            {t({ en: "No preservatives", te: "కల్తీ లేదు" })}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1.4, duration: 0.6 }}
                            className="absolute -left-5 top-1/2 -translate-y-1/2 hidden lg:block"
                        >
                            <div className="bg-[color:var(--kp-paper)] rounded-2xl shadow-lg px-4 py-3 rotate-[-4deg]">
                                <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-brown)]">
                                    {t({ en: "Batch №14", te: "బ్యాచ్ №14" })}
                                </div>
                                <div className="font-display text-lg leading-none mt-1">
                                    {t({ en: "Sun-cured", te: "ఎండలో" })}
                                    <br />
                                    <em className="italic text-[color:var(--kp-terracotta)]">
                                        {t({ en: "for 4 days", te: "4 రోజులు" })}
                                    </em>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
